import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { StreamingAcceleratorMonth } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { acceleratorBars } from '../../periodSections';
import { formatMonth } from '../../streamingFormat';
import { acceleratorMessages as messages } from '../../acceleratorMessages';
import { StreamingBlock } from '../../parts/StreamingBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import shared from '../../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AcceleratorMonthBarsProps {
  byMonth: readonly StreamingAcceleratorMonth[];
}

/**
 * "Streams per month" (design streaming-sections d1): the Accelerator part on top, the other
 * streams under it, and the share on top of the bar. A month with no overall number has no bar
 * and a dash; a 0 is a real zero bar.
 */
const AcceleratorMonthBarsComponent: React.FC<AcceleratorMonthBarsProps> = ({ byMonth }) => {
  const intl = useIntl();
  const bars = useMemo(() => acceleratorBars(byMonth), [byMonth]);

  return (
    <StreamingBlock title={intl.formatMessage(messages.perMonth)} testId="streaming-accelerator-months">
      <div className={styles.accBars} role="list">
        {bars.map((bar) => (
          <div key={bar.month} className={styles.stackBar} role="listitem" data-missing={bar.share === null}>
            <b>
              {bar.share === null ? '—' : intl.formatNumber(bar.share, { style: 'percent', maximumFractionDigits: 0 })}
            </b>
            {bar.height !== null && bar.share !== null ? (
              <span className={styles.accColumn} style={{ height: `${Math.round(bar.height * 80)}%` }}>
                {bar.share > 0 ? <span className={styles.accPart} style={{ height: `${bar.share * 100}%` }} /> : null}
                <span
                  className={clsx(styles.accOther, bar.share === 0 && styles.accOtherSolo)}
                  style={{ height: `${(1 - bar.share) * 100}%` }}
                />
              </span>
            ) : null}
            {formatMonth(intl, bar.month)}
          </div>
        ))}
      </div>
      <div className={styles.legend}>
        <span>
          <span className={styles.swatch} style={{ background: 'var(--brand-grad)' }} />
          {intl.formatMessage(messages.fromAccelerator)}
        </span>
        <span>
          <span className={styles.swatch} style={{ background: 'var(--bg-3)' }} />
          {intl.formatMessage(messages.otherStreams)}
        </span>
      </div>
      <p className={shared.footnote}>{intl.formatMessage(messages.barsNote)}</p>
    </StreamingBlock>
  );
};

export const AcceleratorMonthBars = TypedMemo(AcceleratorMonthBarsComponent);
